import { lazy, Suspense } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { useUi } from '@/stores/ui';
import { ProfileModal } from './ProfileModal';
import { InviteModal } from './InviteModal';
import { QuickSwitcher } from './QuickSwitcher';
import { ShortcutsModal } from './ShortcutsModal';
import { CreateThreadModal } from './CreateThreadModal';
import { EventsModal } from '@/components/events/EventsModal';
import { CustomStatusModal } from './CustomStatusModal';
import { NicknameModal } from './NicknameModal';
import { ServerProfileModal } from './ServerProfileModal';
import { PollModal } from './PollModal';
import { NewDmModal } from './NewDmModal';
import { WelcomeTour } from '@/components/gettingStarted/WelcomeTour';
import { EditHistoryModal } from './EditHistoryModal';
import { FeedbackModal } from './FeedbackModal';
import { ForwardModal } from './ForwardModal';
import { PickRolesModal } from './PickRolesModal';
import { RemindMeModal } from './RemindMeModal';
import { ReportModal } from './ReportModal';
import { TimeoutModal } from './TimeoutModal';
import { CategoryModal, ChannelSettingsModal, CreateChannelModal, CreateServerModal, JoinServerModal } from './ServerModals';

// The settings screens are large and rarely opened, so they load on demand.
const UserSettingsModal = lazy(() => import('@/components/settings/UserSettingsModal').then((m) => ({ default: m.UserSettingsModal })));
const ServerSettingsModal = lazy(() => import('@/components/settings/ServerSettingsModal').then((m) => ({ default: m.ServerSettingsModal })));

function LoadingOverlay() {
  return (
    <div role="status" aria-label="Loading" className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black/40 text-white">
      <Spinner size={28} />
    </div>
  );
}

/** Renders whichever modal the UI store says is open. */
export function ModalHost() {
  const modal = useUi((s) => s.modal);
  if (!modal) return null;
  return (
    <Suspense fallback={<LoadingOverlay />}>
      <CurrentModal />
    </Suspense>
  );
}

function CurrentModal() {
  const modal = useUi((s) => s.modal);
  const close = useUi((s) => s.closeModal);
  if (!modal) return null;

  switch (modal.type) {
    case 'createServer':
      return <CreateServerModal onClose={close} />;
    case 'joinServer':
      return <JoinServerModal onClose={close} initialCode={modal.code} />;
    case 'invite':
      return <InviteModal serverId={modal.serverId} onClose={close} />;
    case 'createChannel':
      return <CreateChannelModal serverId={modal.serverId} initialType={modal.channelType} initialCategoryId={modal.categoryId} onClose={close} />;
    case 'category':
      return <CategoryModal serverId={modal.serverId} categoryId={modal.categoryId} onClose={close} />;
    case 'channelSettings':
      return <ChannelSettingsModal channelId={modal.channelId} onClose={close} />;
    case 'serverSettings':
      return <ServerSettingsModal serverId={modal.serverId} initialSection={modal.section} onClose={close} />;
    case 'userSettings':
      return <UserSettingsModal initialSection={modal.section} onClose={close} />;
    case 'profile':
      return <ProfileModal key={modal.userId} userId={modal.userId} serverId={modal.serverId} onClose={close} />;
    case 'newDm':
      return <NewDmModal conversationId={modal.conversationId} onClose={close} />;
    case 'quickSwitcher':
      return <QuickSwitcher onClose={close} />;
    case 'shortcuts':
      return <ShortcutsModal onClose={close} />;
    case 'customStatus':
      return <CustomStatusModal onClose={close} />;
    case 'nickname':
      return <NicknameModal serverId={modal.serverId} userId={modal.userId} onClose={close} />;
    case 'serverProfile':
      return <ServerProfileModal serverId={modal.serverId} onClose={close} />;
    case 'timeout':
      return <TimeoutModal serverId={modal.serverId} userId={modal.userId} onClose={close} />;
    case 'report':
      return <ReportModal messageId={modal.messageId} userId={modal.userId} subject={modal.subject} onClose={close} />;
    case 'createPoll':
      return <PollModal target={modal.target} onClose={close} />;
    case 'createThread':
      return <CreateThreadModal channelId={modal.channelId} message={modal.message} onClose={close} />;
    case 'remindMe':
      return <RemindMeModal message={modal.message} onClose={close} />;
    case 'editHistory':
      return <EditHistoryModal message={modal.message} onClose={close} />;
    case 'welcomeTour':
      return <WelcomeTour onClose={close} />;
    case 'feedback':
      return <FeedbackModal onClose={close} />;
    case 'pickRoles':
      return <PickRolesModal serverId={modal.serverId} onClose={close} />;
    case 'forward':
      return <ForwardModal message={modal.message} onClose={close} />;
    case 'events':
      return <EventsModal serverId={modal.serverId} eventId={modal.eventId} startCreating={modal.create} onClose={close} />;
  }
}
